import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Plus,
  Trash2,
  Package,
  GripVertical,
  X,
  Save,
  ChevronRight,
  Layers,
  Star,
} from 'lucide-react';

const QuoteBuilderPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [addProductDialog, setAddProductDialog] = useState(false);
  const [newProductName, setNewProductName] = useState('');

  const [editedProduct, setEditedProduct] = useState('');
  const [editedFields, setEditedFields] = useState([]);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/product-configs`);
      setProducts(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setEditedProduct(product.product);
    setEditedFields(product.fields.map((f) => ({ ...f })));
    setError('');
    setSuccess('');
  };

  const handleAddProduct = async () => {
    if (!newProductName.trim()) { setError('Product name is required'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-configs`,
        { product: newProductName, fields: [], is_active: true },
        { withCredentials: true }
      );
      setSuccess('Product created successfully');
      setNewProductName('');
      setAddProductDialog(false);
      await fetchProducts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Delete "${productName}"? This cannot be undone.`)) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/product-configs/${productId}`, { withCredentials: true });
      setSuccess('Product deleted');
      if (selectedProduct?.id === productId) setSelectedProduct(null);
      fetchProducts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    setEditedFields([...editedFields, { name: '', type: 'text', options: [], placeholder: '', required: true }]);
  };

  const handleUpdateField = (index, key, value) => {
    const updated = [...editedFields];
    updated[index] = { ...updated[index], [key]: value };
    setEditedFields(updated);
  };

  const handleDeleteField = (index) => {
    setEditedFields(editedFields.filter((_, i) => i !== index));
  };

  const handleAddOption = (fieldIndex) => {
    const updated = [...editedFields];
    updated[fieldIndex] = { ...updated[fieldIndex], options: [...(updated[fieldIndex].options || []), { name: '', isPopular: false }] };
    setEditedFields(updated);
  };

  const handleUpdateOption = (fieldIndex, optionIndex, value) => {
    const updated = [...editedFields];
    const newOptions = [...updated[fieldIndex].options];
    const opt = newOptions[optionIndex];
    newOptions[optionIndex] = typeof opt === 'object' ? { ...opt, name: value } : { name: value };
    updated[fieldIndex] = { ...updated[fieldIndex], options: newOptions };
    setEditedFields(updated);
  };

  const handleTogglePopular = (fieldIndex, optionIndex) => {
    const updated = [...editedFields];
    const newOptions = [...updated[fieldIndex].options];
    const opt = newOptions[optionIndex];
    const currentPopular = typeof opt === 'object' ? !!opt.isPopular : false;
    newOptions[optionIndex] = typeof opt === 'object' ? { ...opt, isPopular: !currentPopular } : { name: opt, isPopular: true };
    updated[fieldIndex] = { ...updated[fieldIndex], options: newOptions };
    setEditedFields(updated);
  };

  const handleDeleteOption = (fieldIndex, optionIndex) => {
    const updated = [...editedFields];
    updated[fieldIndex] = { ...updated[fieldIndex], options: updated[fieldIndex].options.filter((_, i) => i !== optionIndex) };
    setEditedFields(updated);
  };

  const handleSaveChanges = async () => {
    if (!editedProduct.trim()) { setError('Product name cannot be empty'); return; }
    for (let i = 0; i < editedFields.length; i++) {
      if (!editedFields[i].name.trim()) { setError(`Field ${i + 1} name cannot be empty`); return; }
    }
    setLoading(true);
    setError('');
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-configs/${selectedProduct.id}`,
        { product: editedProduct, fields: editedFields, is_active: selectedProduct.is_active },
        { withCredentials: true }
      );
      setSuccess('Changes saved successfully');
      const updatedProducts = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/product-configs`);
      setProducts(updatedProducts.data);
      const updated = updatedProducts.data.find((p) => p.id === selectedProduct.id);
      if (updated) setSelectedProduct(updated);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (productId, currentStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-configs/${productId}`,
        { is_active: !currentStatus },
        { withCredentials: true }
      );
      fetchProducts();
      if (selectedProduct?.id === productId) setSelectedProduct({ ...selectedProduct, is_active: !currentStatus });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to toggle status');
    }
  };

  return (
    <div className="p-8" data-testid="quote-builder-content">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#121212]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Quote Builder
        </h1>
        <p className="text-sm text-[#808080] mt-1">Manage products and customize quote form fields</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5">{error}<button onClick={() => setError('')} className="ml-3 underline text-sm">Dismiss</button></div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-5">{success}</div>}

      {/* Split Layout */}
      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* LEFT: Product List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[#808080] uppercase tracking-wider">Products</h2>
            <Dialog open={addProductDialog} onOpenChange={setAddProductDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#C69C6D] hover:bg-[#B38B5D] text-black rounded-lg h-8" data-testid="add-product-button">
                  <Plus size={14} className="mr-1" />Add
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Create New Product</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label>Product Name *</Label>
                    <Input value={newProductName} onChange={(e) => setNewProductName(e.target.value)} placeholder="e.g., Premium Business Cards" className="mt-1.5" data-testid="new-product-name-input" />
                  </div>
                  <Button onClick={handleAddProduct} disabled={loading} className="w-full bg-[#C69C6D] hover:bg-[#B38B5D] text-black font-semibold" data-testid="submit-new-product">
                    {loading ? 'Creating...' : 'Create Product'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-2">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => handleSelectProduct(product)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                  selectedProduct?.id === product.id
                    ? 'bg-white border-[#C69C6D] shadow-md'
                    : 'bg-white border-[#EBEBEB] hover:border-[#C69C6D]/40 hover:shadow-sm'
                }`}
                data-testid={`product-card-${product.id}`}
              >
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedProduct?.id === product.id ? 'bg-[#C69C6D]/15' : 'bg-[#F5F5F0]'}`}>
                      <Package size={16} className={selectedProduct?.id === product.id ? 'text-[#C69C6D]' : 'text-[#808080]'} />
                    </div>
                    <h3 className="font-medium text-sm text-[#121212]">{product.product}</h3>
                  </div>
                  {selectedProduct?.id === product.id && <ChevronRight size={14} className="text-[#C69C6D] mt-1" />}
                </div>
                <div className="flex items-center gap-3 ml-[42px]">
                  <span className="text-xs text-[#808080]">{product.fields.length} fields</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${product.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </button>
            ))}
            {products.length === 0 && (
              <div className="text-center py-12 text-[#808080]">
                <Package size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No products yet</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Product Editor */}
        <div>
          {selectedProduct ? (
            <div className="bg-white rounded-xl border border-[#EBEBEB] shadow-sm overflow-hidden">
              {/* Product Header */}
              <div className="p-6 border-b border-[#EBEBEB] bg-[#FAFAF8]">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-xs font-semibold text-[#808080] uppercase tracking-wider">Product Name</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#808080]">Active</span>
                    <Switch
                      checked={selectedProduct.is_active}
                      onCheckedChange={() => handleToggleActive(selectedProduct.id, selectedProduct.is_active)}
                      data-testid="toggle-product-active"
                    />
                  </div>
                </div>
                <Input
                  value={editedProduct}
                  onChange={(e) => setEditedProduct(e.target.value)}
                  className="text-lg font-semibold bg-white"
                  data-testid="edit-product-name-input"
                />
              </div>

              {/* Fields Section */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Layers size={16} className="text-[#808080]" />
                    <h3 className="font-semibold text-sm text-[#121212]">Form Fields</h3>
                    <span className="text-xs bg-[#F5F5F0] text-[#808080] px-2 py-0.5 rounded-full">{editedFields.length}</span>
                  </div>
                  <Button size="sm" onClick={handleAddField} variant="outline" className="rounded-lg h-8" data-testid="add-field-button">
                    <Plus size={14} className="mr-1" />Add Field
                  </Button>
                </div>

                <div className="space-y-4">
                  {editedFields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className="p-5 bg-[#FAFAF8] rounded-xl border border-[#EBEBEB]" data-testid={`field-card-${fieldIndex}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 text-[#808080]">
                          <GripVertical size={16} className="cursor-grab" />
                          <span className="text-xs font-semibold uppercase tracking-wider">Field {fieldIndex + 1}</span>
                        </div>
                        <button onClick={() => handleDeleteField(fieldIndex)} className="p-1.5 rounded-lg hover:bg-red-50 text-[#C0C0C0] hover:text-red-500 transition-colors" data-testid={`delete-field-${fieldIndex}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <Label className="text-xs mb-1.5 block text-[#808080]">Field Name *</Label>
                          <Input value={field.name} onChange={(e) => handleUpdateField(fieldIndex, 'name', e.target.value)} placeholder="e.g., Paper Type" className="bg-white" data-testid={`field-name-${fieldIndex}`} />
                        </div>
                        <div>
                          <Label className="text-xs mb-1.5 block text-[#808080]">Field Type</Label>
                          <Select value={field.type} onValueChange={(value) => handleUpdateField(fieldIndex, 'type', value)}>
                            <SelectTrigger className="bg-white" data-testid={`field-type-${fieldIndex}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="select">Dropdown (Select)</SelectItem>
                              <SelectItem value="text">Text Input</SelectItem>
                              <SelectItem value="number">Number Input</SelectItem>
                              <SelectItem value="textarea">Text Area</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Options Editor (for select type) */}
                      {field.type === 'select' && (
                        <div className="mt-4 pt-4 border-t border-[#EBEBEB]">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-xs text-[#808080]">Dropdown Options</Label>
                            <button onClick={() => handleAddOption(fieldIndex)} className="text-xs text-[#C69C6D] hover:text-[#B38B5D] font-medium" data-testid={`add-option-${fieldIndex}`}>
                              + Add Option
                            </button>
                          </div>
                          <div className="space-y-2.5">
                            {field.options && field.options.map((option, optionIndex) => {
                              const optName = typeof option === 'object' ? option.name : option;
                              const optPopular = typeof option === 'object' ? !!option.isPopular : false;
                              return (
                                <div key={optionIndex} className="flex items-center gap-2">
                                  <Input value={optName} onChange={(e) => handleUpdateOption(fieldIndex, optionIndex, e.target.value)} placeholder={`Option ${optionIndex + 1}`} className="bg-white text-sm flex-1" data-testid={`option-input-${fieldIndex}-${optionIndex}`} />
                                  <button
                                    onClick={() => handleTogglePopular(fieldIndex, optionIndex)}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 whitespace-nowrap ${optPopular ? 'bg-[#C69C6D]/15 border-[#C69C6D]/40 text-[#C69C6D]' : 'bg-white border-[#EBEBEB] text-[#C0C0C0] hover:border-[#C69C6D]/30 hover:text-[#C69C6D]'}`}
                                    data-testid={`toggle-popular-${fieldIndex}-${optionIndex}`}
                                    title="Mark as Popular"
                                  >
                                    <Star size={12} className={optPopular ? 'fill-current' : ''} />
                                    Popular
                                  </button>
                                  <button onClick={() => handleDeleteOption(fieldIndex, optionIndex)} className="p-1.5 text-[#C0C0C0] hover:text-red-500 transition-colors" data-testid={`delete-option-${fieldIndex}-${optionIndex}`}>
                                    <X size={14} />
                                  </button>
                                </div>
                              );
                            })}
                            {(!field.options || field.options.length === 0) && (
                              <p className="text-xs text-[#C0C0C0] py-2">No options yet. Click "+ Add Option" above.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {editedFields.length === 0 && (
                    <div className="text-center py-12 text-[#808080] border-2 border-dashed border-[#EBEBEB] rounded-xl">
                      <Layers size={40} className="mx-auto mb-3 opacity-20" />
                      <p className="text-sm font-medium">No fields configured</p>
                      <p className="text-xs mt-1">Click "Add Field" to start building your quote form</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-5 border-t border-[#EBEBEB] bg-[#FAFAF8] flex items-center gap-3">
                <Button onClick={handleSaveChanges} disabled={loading} className="flex-1 bg-[#C69C6D] hover:bg-[#B38B5D] text-black font-semibold py-5 rounded-lg" data-testid="save-product-changes">
                  <Save size={16} className="mr-2" />{loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button onClick={() => handleDeleteProduct(selectedProduct.id, selectedProduct.product)} variant="outline" disabled={loading} className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-lg py-5" data-testid="delete-product-button">
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-dashed border-[#EBEBEB] p-16 text-center">
              <Package size={56} className="mx-auto mb-4 text-[#C69C6D] opacity-25" />
              <h3 className="text-lg font-semibold text-[#121212] mb-2">No Product Selected</h3>
              <p className="text-sm text-[#808080]">Select a product from the list to edit its configuration</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilderPage;
